<template>
  <div class="acl-table" :class="{ readonly: readonly }">
    <table :class="{ 'no-perms': !hasOneSubGroupWithPerms(group), 'acl-table-header': hasOneSubGroupWithPerms(group)}">
      <tr :class="{'no-perms': !hasOneSubGroupWithPerms(group), 'group-header-row alt-cell-bgs': hasOneSubGroupWithPerms(group) }">
        <td>
          <div class="cell">
            <div>
              <strong>{{ group.groupName }}</strong>
              <tool-tip
                right
                style="font-size: 1rem;"
                message="this group has no subgroups with permissions assigned, and will not be added">
                <font-awesome-icon v-if="!hasOneSubGroupWithPerms(group)" class="no-perms-warning" :icon="['fas', 'exclamation-triangle']"></font-awesome-icon>
              </tool-tip>
            </div>
            <div class="_flex align-center">
              <div>{{ group.groupId }}</div>
              <div class="_pls _prs">
                <span v-if="readonly">Primary Contact:
                  <font-awesome-icon  v-if="group.access.primaryContact" class="_c-green-darker" :icon="['fa', 'check']" aria-label="Check"></font-awesome-icon>
                  <font-awesome-icon v-else class="_c-state-error" :icon="['fa', 'times']" aria-label="X"></font-awesome-icon>
                </span>
                <AppCheckbox
                  v-else-if="showPrimaryContact"
                  class="acl-table-subgroup-header"
                  :valueKey='`primaryContact-${group.groupCk}`'
                  label='Primary Contact'
                  v-model="group.access.primaryContact"
                  @click="primaryContactClicked(group.groupId, $event)"
                  />
              </div>
              <div>
                <button v-if="!readonly" @click="removeGroup(group.groupCk)" class="btn white --sm _mxs">Remove</button>
              </div>
            </div>
          </div>
        </td>
        <!-- checkboxes to set perms for ALL subgroups -->
        <td v-for="(perm, idx) in perms" :key="`${perm.name}${idx}`">
          <div class="cell -acl">
            <AppCheckbox
              :value="allSubGroupsHaveAccess(perm.name)"
              @click="togglePermForAllSubGroups(group.groupCk, perm.name, $event)"
              :valueKey='`${group.groupCk}-ALL-${perm.name}`'
              :label="perm.text"
              />
            <div v-if="readonly" class="icon-container _text-center">
              <font-awesome-icon
                v-if="allSubGroupsHaveAccess(perm.name)"
                class="" :icon="['fa', 'check']" aria-label="Check">
              </font-awesome-icon>
            </div>
          </div>
        </td>
        <td>
          <CollapseButton
            :initiallyCollapsed='isCollapsed'
            @onToggle='onCollapseToggled'
            class='_bl0' />
        </td>
      </tr>
      <!-- expanded section -->
      <tbody v-if="!isCollapsed">
        <tr 
          :class="{ 'no-perms': !hasOneSubGroupWithPerms(group), 'alt-cell-bgs': hasOneSubGroupWithPerms(group)}">
          <td>
            <span class="select-all" @click="toggleAll(true)">Select All</span>
          </td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
        <tr v-for="(subGroup, idx) in group.subGroups" 
          :key="idx" 
          :class="{ 'no-perms': hasNoPerms(subGroup.access), 'alt-cell-bgs': !hasNoPerms(subGroup.access)}">
          <td>
            <div class="cell">
              <!-- check box to LEFT of subgroup name -->
              <AppCheckbox
                class="acl-table-subGroup-header"
                @click="togglePermsForSubGroup(subGroup.subGroupCk, $event)"
                :value='hasAllPerms(subGroup.access)'
                :valueKey='`${group.groupCk}-${subGroup.subGroupCk}${idx}`'
                :label='`${subGroup.subGroupName} - ${subGroup.subGroupId}`'
              />
            </div>
          </td>
          <td v-for="(perm, idx) in perms" :key="`${subGroup.subGroupCk}_${idx}_${perm.name}`">
            <div class="cell -acl">
              <font-awesome-icon v-if="readonly && subGroup.access[perm.name]" :icon="['fa', 'check']" aria-label="Check"></font-awesome-icon>
              <AppCheckbox
                v-else
                :value="subGroup.access[perm.name]"
                :valueKey="`${group.groupCk}-${subGroup.subGroupCk}-${perm.name}-${idx}`"
                @click="togglePerm(group.groupCk, subGroup.subGroupCk, perm.name, $event)"
                label=""
              />
            </div>
          </td>
          <td>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
  import { computed, onBeforeMount, ref, watch } from '@vue/composition-api';
  import { useStore } from '@vue3shims';
  import AppCheckbox from '@atoms/Checkbox.vue';
  import CollapseButton from '@molecules/CollapseButton';
  import ToolTip from '@molecules/ToolTip.vue';

  export default {
    setup(props, { emit }) {
      const store = useStore();
      const group = ref(props.group);
      const activeUser = computed(() => store.getters['users2/activeUser']);
      const perms = computed(() => {
        let rawPerms = store.getters['users2/perms'] || [];
        if(hideAdmin.value === true) {
          rawPerms = rawPerms.filter((perm) => perm.name !== 'createGroupUsersAllowed');
        }
        return rawPerms;
      });
      const hideAdmin = computed(() => ['B', 'T'].includes(activeUser.value.accountType));
      const showPrimaryContact = computed(() => activeUser.value.accountType === 'C' || activeUser.value.accountType === 'R');
      const isCollapsed = ref(true);

      watch(() => props.collapsed, (n, o) => isCollapsed.value = n);
      onBeforeMount(() => {
        // console.log(`ACLTable`, group.value);
      });

      const allSubGroupsHaveAccess = (perm) => {
        let subsWithAccess = []
        let totalSubGroups = 0
        props.group.subGroups.forEach((subGroup) => {
          totalSubGroups++;
          if(subGroup.access[perm] === true) {
            subsWithAccess.push(subGroup);
          }
        });

        return subsWithAccess.length === totalSubGroups;
      }
      const hasAllPerms = (access) => {
        let values = [];
        Object.entries(access).forEach(([k, v]) => {
          if(k !== 'primaryContact') {
            values.push(v);
          }
        });
        // console.log('hasAllPerms', values);
        return !values.includes(false);
      }

      const hasNoPerms = (access) => {
        let values = [];
        Object.entries(access).forEach(([k, v]) => {
          if(k !== 'primaryContact') {
            values.push(v);
          }
        });
        return !values.includes(true);
      }

      const hasOneSubGroupWithPerms = (group) => {
        let sgsWithAPerm = group.subGroups.filter((sg) => {
          let values = [];
          Object.entries(sg.access).forEach(([k, v]) => {
            if(k !== 'primaryContact') {
              values.push(v);
            }
          });
          return values.includes(true);
        });
        return sgsWithAPerm.length > 0;
      };

      const onCollapseToggled = () => {
        isCollapsed.value = !isCollapsed.value;
      // console.log('isCollapsed:', isCollapsed.value);
      }

      const primaryContactClicked = (group, isPrimaryContact) => {
        emit('acl-change', {perm: 'primaryContact', value: isPrimaryContact, groupId: group});
      }

      const removeGroup = (removeCk) => {
        emit('remove-group', removeCk);
      }

      const togglePermForAllSubGroups = (groupCk, perm, value) => {
        // console.log('togglePermForAllSubGroups', groupCk, perm, value);
        emit('acl-change', {subGroupCk: '__ALL__', groupCk, perm, value });
      }

      const togglePermsForSubGroup = (subGroupCk, value) => {
        // console.log('togglePermsForSubGroup', subGroupCk, value);
        emit('acl-change', {subGroupCk, perm: '__ALL__', value });
      }

      const togglePerm = (groupCk, subGroupCk, perm, value) => {
        // console.log('togglePerm', subGroupCk, perm, value);
        emit('acl-change', { groupCk, subGroupCk, perm, value });
      }

      const toggleAll = (value) => {
        emit('acl-change', {groupCk: group.value.groupCk, subGroupCk: '__ALL__', perm: '__ALL__', value });
      }

      return {
        allSubGroupsHaveAccess,
        hasAllPerms,
        hasNoPerms,
        hasOneSubGroupWithPerms,
        hideAdmin,
        isCollapsed,
        onCollapseToggled,
        perms,
        primaryContactClicked,
        removeGroup,
        showPrimaryContact,
        toggleAll,
        togglePerm,
        togglePermForAllSubGroups,
        togglePermsForSubGroup,
      }
    },
    emits: ['acl-change', 'remove-group'],
    props: {
      collapsed: {
        type: Boolean,
        default: true
      },
      group: {
        type: Object,
        required: true,
      },
      readonly: {
        type: Boolean,
        required: false,
        default: () => false,
      }
    },
    components: {
      AppCheckbox,
      CollapseButton,
      ToolTip,
    },
  }
</script>

<style lang="scss">
.no-perms {
  background-color: $color-light-gray-alt;
  color: $color-search-text;
}
.no-perms-warning {
  margin-left: 0.3rem;
  color: $color-search-text;
}
</style>